import React, { useState, useEffect, useCallback } from 'react';
import {
    X,
    User,
    Mail,
    Phone,
    Calendar,
    Briefcase,
    FileText,
    ChevronDown,
    ChevronUp,
    Shield,
    ArrowRight,
    Users,
    UserPlus,
    Plus,
    Check,
    Settings,
    Info,
    Heart,
    Building2,
    Trash2,
    Pencil,
} from 'lucide-react';
import EmailPreviewModal from './EmailPreviewModal';
import CreateRoleModal from './CreateRoleModal';
import UserSearchDropdown from './UserSearchDropdown';
import api from '../utils/api';

/**
 * UnifiedAddEmployeeModal - Single modal for adding all types of users
 * 
 * 6-Section Accordion Form:
 * 1. Basic Info (first name, last name, email, phone)
 * 2. Role & Permissions (account type, role dropdown, inline role creation)
 * 3. Organizational Structure (team, manager)
 * 4. Onboarding Support Team (team lead, buddy, HR contact)
 * 5. Onboarding Journey (lifecycle stage, start date)
 * 6. Additional Details (notes)
 */
const UnifiedAddEmployeeModal = ({ isOpen, onClose, onSuccess, companyData }) => {
    const [loading, setLoading] = useState(false);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [availableTeams, setAvailableTeams] = useState([]);
    const [permissionCategories, setPermissionCategories] = useState([]);

    // Custom placeholders
    const [customPlaceholders, setCustomPlaceholders] = useState([]);
    const [showAddPlaceholder, setShowAddPlaceholder] = useState(false);
    const [newPlaceholderName, setNewPlaceholderName] = useState('');
    const [newPlaceholderDescription, setNewPlaceholderDescription] = useState('');
    const [editingPlaceholder, setEditingPlaceholder] = useState(null);
    const [customPlaceholderAssignments, setCustomPlaceholderAssignments] = useState({});

    // Accordion state — which sections are expanded
    const [expandedSections, setExpandedSections] = useState({
        basicInfo: true,
        rolePermissions: true,
        orgStructure: false,
        supportTeam: false,
        journey: true,
        additionalDetails: false,
    });

    // Modal state
    const [showEmailPreview, setShowEmailPreview] = useState(false);
    const [pendingUserData, setPendingUserData] = useState(null);
    const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
    const [inlineRoleData, setInlineRoleData] = useState(null);

    // Inline role creation state
    const [showInlineRoleCreation, setShowInlineRoleCreation] = useState(false);
    const [inlineRoleName, setInlineRoleName] = useState('');
    const [inlineRoleDescription, setInlineRoleDescription] = useState('');
    const [inlineRolePermissions, setInlineRolePermissions] = useState({});

    // Validation
    const [validationErrors, setValidationErrors] = useState({});
    const [touchedFields, setTouchedFields] = useState({});

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[\d\s\-\(\)\+]{7,}$/;

    // Form state
    const [formData, setFormData] = useState({
        account_type: 'employee',
        lifecycle_stage: 'onboarding',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        start_date: '',
        role_id: null,
        role: '',
        team_ids: [],
        // Support team — UserSearchDropdown values (objects with id, first_name, etc.)
        manager: null,
        team_lead: null,
        buddy: null,
        hr_contact: null,
        additional_details: '',
    });

    // Helper functions
    const getTodayDate = () => new Date().toISOString().split('T')[0];

    const getDefaultStartDate = () => {
        const date = new Date();
        date.setDate(date.getDate() + 7);
        return date.toISOString().split('T')[0];
    };

    const getTomorrowDate = () => {
        const date = new Date();
        date.setDate(date.getDate() + 1);
        return date.toISOString().split('T')[0];
    };

    // Fetch data on open
    useEffect(() => {
        if (isOpen) {
            fetchRoles();
            fetchTeams();
            fetchPermissionCategories();
            fetchCustomPlaceholders();
            resetForm();
        }
    }, [isOpen]);

    const fetchRoles = async () => {
        try {
            const response = await api.get('/employees/roles');
            setAvailableRoles(response.data.roles || []);
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    const fetchTeams = async () => {
        try {
            const response = await api.get('/teams');
            setAvailableTeams(response.data.teams || []);
        } catch (error) {
            console.error('Error fetching teams:', error);
        }
    };

    const fetchPermissionCategories = async () => {
        try {
            const response = await api.get('/employees/roles/templates');
            setPermissionCategories(response.data.categories || []);
        } catch (error) {
            console.error('Error fetching permission categories:', error);
        }
    };

    const fetchCustomPlaceholders = async () => {
        try {
            const response = await api.get('/placeholders');
            setCustomPlaceholders(response.data.placeholders || []);
        } catch (error) {
            console.error('Error fetching custom placeholders:', error);
        }
    };

    const handleCreatePlaceholder = async () => {
        if (!newPlaceholderName.trim()) return;
        try {
            await api.post('/placeholders', {
                name: newPlaceholderName.trim(),
                description: newPlaceholderDescription.trim() || null,
            });
            await fetchCustomPlaceholders();
            setNewPlaceholderName('');
            setNewPlaceholderDescription('');
            setShowAddPlaceholder(false);
        } catch (error) {
            console.error('Error creating placeholder:', error);
        }
    };

    const handleUpdatePlaceholder = async (id) => {
        if (!editingPlaceholder?.name?.trim()) return;
        try {
            await api.put(`/placeholders/${id}`, {
                name: editingPlaceholder.name.trim(),
                description: editingPlaceholder.description?.trim() || null,
            });
            await fetchCustomPlaceholders();
            setEditingPlaceholder(null);
        } catch (error) {
            console.error('Error updating placeholder:', error);
        }
    };

    const handleDeletePlaceholder = async (id) => {
        try {
            await api.delete(`/placeholders/${id}`);
            await fetchCustomPlaceholders();
            // Remove assignment if exists
            setCustomPlaceholderAssignments(prev => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
        } catch (error) {
            console.error('Error deleting placeholder:', error);
        }
    };

    // Validation
    const validateField = (name, value) => {
        switch (name) {
            case 'first_name':
                return !value?.trim() ? 'First name is required' : '';
            case 'last_name':
                return !value?.trim() ? 'Last name is required' : '';
            case 'email':
                if (!value?.trim()) return 'Email is required';
                if (!emailRegex.test(value)) return 'Invalid email format';
                return '';
            case 'phone':
                if (value && !phoneRegex.test(value)) return 'Invalid phone format';
                return '';
            case 'start_date':
                if (formData.account_type === 'employee' && formData.lifecycle_stage === 'preboarding' && !value) {
                    return 'Start date is required for preboarding';
                }
                return '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const errors = {};
        ['first_name', 'last_name', 'email', 'phone', 'start_date'].forEach(field => {
            const error = validateField(field, formData[field]);
            if (error) errors[field] = error;
        });
        return errors;
    };

    // Handlers
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (touchedFields[name]) {
            const error = validateField(name, value);
            setValidationErrors(prev => ({ ...prev, [name]: error }));
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setTouchedFields(prev => ({ ...prev, [name]: true }));
        const error = validateField(name, value);
        setValidationErrors(prev => ({ ...prev, [name]: error }));
    };

    const handleAccountTypeChange = (type) => {
        setFormData(prev => ({
            ...prev,
            account_type: type,
            lifecycle_stage: type === 'admin' ? 'active' : 'onboarding',
            role_id: null,
            role: '',
        }));
        setShowInlineRoleCreation(false);
    };

    const handleLifecycleStageChange = (stage) => {
        setFormData(prev => ({
            ...prev,
            lifecycle_stage: stage,
            start_date: stage === 'preboarding' ? prev.start_date || getDefaultStartDate() : '',
        }));
    };

    const handleRoleChange = (e) => {
        const value = e.target.value;

        if (value === 'create_new') {
            setShowInlineRoleCreation(true);
            setInlineRoleName('');
            setInlineRoleDescription('');
            setInlineRolePermissions({});
        } else {
            setShowInlineRoleCreation(false);
            const selectedRole = availableRoles.find(r => typeof r === 'object' && r.id === parseInt(value));
            setFormData(prev => ({
                ...prev,
                role_id: selectedRole ? selectedRole.id : null,
                role: selectedRole ? selectedRole.role_name : value,
            }));
        }
    };

    const handleTeamToggle = (teamId) => {
        setFormData(prev => ({
            ...prev,
            team_ids: prev.team_ids.includes(teamId)
                ? prev.team_ids.filter(id => id !== teamId)
                : [...prev.team_ids, teamId],
        }));
    };

    // Inline role creation handlers
    const handleInlinePermissionToggle = (categoryKey) => {
        setInlineRolePermissions(prev => ({
            ...prev,
            [categoryKey]: !prev[categoryKey],
        }));
    };

    const handleCreateRoleInline = async () => {
        if (!inlineRoleName.trim()) return;

        setLoading(true);
        try {
            // Build permissions from toggled categories
            const permissions = {};
            permissionCategories.forEach(category => {
                if (inlineRolePermissions[category.key]) {
                    if (category.permissions) {
                        category.permissions.forEach(perm => {
                            permissions[perm.key] = true;
                        });
                    }
                }
            });

            const response = await api.post('/employees/roles', {
                role_name: inlineRoleName.trim(),
                description: inlineRoleDescription.trim(),
                permissions,
            });

            if (response.data.role) {
                await fetchRoles();
                setFormData(prev => ({
                    ...prev,
                    role_id: response.data.role.id,
                    role: response.data.role.role_name,
                }));
                setShowInlineRoleCreation(false);
            }
        } catch (error) {
            console.error('Error creating role:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCustomisePermissions = () => {
        setInlineRoleData({
            role_name: inlineRoleName,
            description: inlineRoleDescription,
            permissions: inlineRolePermissions,
        });
        setShowCreateRoleModal(true);
    };

    const handleFullRoleCreated = async () => {
        await fetchRoles();
        setShowCreateRoleModal(false);
        setShowInlineRoleCreation(false);
        setInlineRoleData(null);

        // Try to select the newly created role
        try {
            const response = await api.get('/employees/roles');
            const roles = response.data.roles || [];
            const newest = roles.reduce((latest, role) => {
                if (!latest || new Date(role.created_at) > new Date(latest.created_at)) {
                    return role;
                }
                return latest;
            }, null);

            if (newest) {
                setFormData(prev => ({
                    ...prev,
                    role_id: newest.id,
                    role: newest.role_name,
                }));
            }
        } catch (err) {
            console.error('Error fetching newest role:', err);
        }
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        const errors = validateForm();
        setValidationErrors(errors);
        setTouchedFields({
            first_name: true,
            last_name: true,
            email: true,
            phone: true,
            start_date: true,
        });

        if (Object.values(errors).some((err) => err)) {
            // Auto-expand sections with errors
            setExpandedSections(prev => ({ ...prev, basicInfo: true }));
            return;
        }

        // Determine lifecycle_stage based on selections
        let lifecycle_stage = formData.lifecycle_stage;

        if (formData.account_type === 'admin') {
            lifecycle_stage = 'active';
        }

        // Build custom placeholder assignments array
        const placeholderAssignments = Object.entries(customPlaceholderAssignments)
            .filter(([_, user]) => user?.id)
            .map(([placeholderId, user]) => ({
                placeholder_id: parseInt(placeholderId),
                assigned_user_id: user.id,
            }));

        const userData = {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            phone: formData.phone,
            role: formData.role,
            role_id: formData.role_id,
            manager_id: formData.manager?.id || null,
            team_lead_id: formData.team_lead?.id || null,
            buddy_id: formData.buddy?.id || null,
            hr_contact_id: formData.hr_contact?.id || null,
            additional_details: formData.additional_details,
            lifecycle_stage,
            team_ids: formData.team_ids,
            placeholder_assignments: placeholderAssignments.length > 0 ? placeholderAssignments : undefined,
        };

        // Add start date for preboarding
        if (formData.lifecycle_stage === 'preboarding' && formData.start_date) {
            userData.start_date = formData.start_date;
            userData.preboarding = {
                preboarding_start_date: getTodayDate(),
                employee_actual_start_date: formData.start_date,
            };
        } else if (formData.lifecycle_stage === 'onboarding') {
            userData.start_date = getTodayDate();
        }

        setPendingUserData(userData);
        setShowEmailPreview(true);
    };

    const handleEmailSend = async ({ emailContent, sendType, scheduledDateTime }) => {
        setLoading(true);
        try {
            const payload = {
                ...pendingUserData,
                emailContent,
                sendType,
                scheduledDateTime,
            };

            await api.post('/employees', payload);
            resetForm();
            onClose();
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Error creating user:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            account_type: 'employee',
            lifecycle_stage: 'onboarding',
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            start_date: '',
            role_id: null,
            role: '',
            team_ids: [],
            manager: null,
            team_lead: null,
            buddy: null,
            hr_contact: null,
            additional_details: '',
        });
        setExpandedSections({
            basicInfo: true,
            rolePermissions: true,
            orgStructure: false,
            supportTeam: false,
            journey: true,
            additionalDetails: false,
        });
        setShowInlineRoleCreation(false);
        setShowEmailPreview(false);
        setPendingUserData(null);
        setValidationErrors({});
        setTouchedFields({});
        setInlineRoleName('');
        setInlineRoleDescription('');
        setInlineRolePermissions({});
        setCustomPlaceholderAssignments({});
        setShowAddPlaceholder(false);
        setNewPlaceholderName('');
        setNewPlaceholderDescription('');
        setEditingPlaceholder(null);
    };

    // Get filtered roles based on account type
    const getFilteredRoles = () => {
        if (formData.account_type === 'admin') {
            return availableRoles.filter(role => {
                if (typeof role === 'string') return true;
                const perms = role.permissions || {};
                return perms['system.settings'] || perms['employee.add'] || perms['role.create'];
            });
        }
        return availableRoles;
    };

    // Permission category labels for inline role creation
    const permissionCategoryLabels = {
        employee: 'Employee Management',
        task: 'Task Management',
        file: 'File Management',
        notice: 'Notices & Communication',
        analytics: 'Analytics & Reporting',
        system: 'System Administration',
    };

    // Get selected role's permissions for preview
    const getSelectedRolePermissions = () => {
        if (!formData.role_id) return null;
        const selectedRole = availableRoles.find(r =>
            typeof r === 'object' && r.id === formData.role_id
        );
        return selectedRole?.permissions || null;
    };

    // Permission display labels mapping
    const permissionDisplayLabels = {
        'employee.add': 'Add employees', 'employee.edit': 'Edit employees',
        'employee.delete': 'Delete employees', 'employee.view_details': 'View employee details',
        'employee.change_roles': 'Change employee roles', 'employee.lifecycle': 'Manage lifecycle',
        'employee.view_history': 'View employee history', 'employee.mark_complete': 'Mark onboarding complete',
        'employee.reactivate': 'Reactivate employees',
        'task.create': 'Create tasks', 'task.view': 'View tasks', 'task.edit': 'Edit tasks',
        'task.delete': 'Delete tasks', 'task.assign': 'Assign tasks', 'task.review': 'Review submissions',
        'task.template_create': 'Create templates', 'task.template_edit': 'Edit templates',
        'task.template_use': 'Use templates', 'task.template_schedule': 'Schedule templates',
        'task.draft_manage': 'Manage drafts', 'task.bulk_actions': 'Bulk actions',
        'file.upload': 'Upload files', 'file.view': 'View files', 'file.edit': 'Edit files',
        'file.delete': 'Delete files', 'file.category_manage': 'Manage categories',
        'file.view_history': 'View file history', 'file.folder_manage': 'Manage folders',
        'file.integration_import': 'Import from integrations', 'file.bulk_download': 'Bulk download',
        'notice.create': 'Create notices', 'notice.view': 'View notices', 'notice.edit': 'Edit notices',
        'notice.delete': 'Delete notices', 'notice.view_analytics': 'View notice analytics',
        'notice.messaging': 'Notice messaging',
        'course.create': 'Create courses', 'course.view': 'View courses', 'course.edit': 'Edit courses',
        'course.delete': 'Delete courses', 'course.enrollment': 'Manage enrollment',
        'course.templates': 'Manage templates', 'course.view_analytics': 'View course analytics',
        'course.draft_manage': 'Manage drafts',
        'compliance.full_manage': 'Full compliance management',
        'compliance.create_tasks': 'Create compliance tasks',
        'compliance.view_reports': 'View compliance reports',
        'compliance.export_reports': 'Export compliance reports',
        'compliance.resolve_violations': 'Resolve violations',
        'analytics.company_wide': 'Company-wide analytics',
        'analytics.employee_progress': 'Employee progress',
        'analytics.task_analytics': 'Task analytics', 'analytics.file_analytics': 'File analytics',
        'analytics.ai_metrics': 'AI assistant metrics', 'analytics.login_stats': 'Login statistics',
        'analytics.productivity': 'Productivity metrics', 'analytics.export': 'Export analytics',
        'feedback.view': 'View feedback', 'feedback.respond': 'Respond to feedback',
        'survey.create': 'Create surveys', 'survey.edit': 'Edit surveys',
        'survey.view_responses': 'View survey responses',
        'survey.export_responses': 'Export survey responses', 'survey.delete': 'Delete surveys',
        'survey.manage_recurring': 'Manage recurring surveys',
        'role.create': 'Create roles', 'role.edit': 'Edit roles', 'role.delete': 'Delete roles',
        'role.assign': 'Assign roles',
        'team.create': 'Create teams', 'team.edit': 'Edit teams', 'team.delete': 'Delete teams',
        'team.manage_members': 'Manage team members',
        'system.settings': 'Manage settings', 'system.integrations': 'Manage integrations',
        'system.permission_config': 'Configure permissions', 'system.audit_view': 'View audit logs',
        'system.audit_export': 'Export audit logs', 'system.notifications': 'Manage notifications',
        'system.email_templates': 'Manage email templates', 'system.health': 'View system health',
        'system.billing': 'Manage billing', 'system.backup': 'Manage backups',
        'system.api_keys': 'Manage API keys',
        'ai.use_assistant': 'Use AI assistant', 'ai.start_conversations': 'Start AI conversations',
        'ai.upload_files': 'Upload files to AI', 'ai.task_analysis': 'AI task analysis',
        'ai.file_analysis': 'AI file analysis',
        'lifecycle.create_preboarding': 'Create preboarding content',
        'lifecycle.view_preboarding': 'View preboarding content',
        'lifecycle.create_onboarding': 'Create onboarding content',
        'lifecycle.view_onboarding': 'View onboarding content',
    };

    // Group permissions by category for display
    const groupPermissionsByCategory = (permissions) => {
        const categories = {
            'Employee Management': [], 'Task Management': [], 'File Management': [],
            'Notices': [], 'Courses': [], 'Compliance': [],
            'Analytics': [], 'Feedback & Surveys': [],
            'Roles & Teams': [], 'System Admin': [], 'AI Assistant': [], 'Lifecycle': [],
        };

        Object.entries(permissions).forEach(([key, value]) => {
            if (!value || value === 'none') return;
            const label = permissionDisplayLabels[key] || key;
            const scope = typeof value === 'string' && value !== 'true' ? value : null;
            const displayValue = scope ? `${label} (${scope.replace('_', ' ')})` : label;

            if (key.startsWith('employee.')) categories['Employee Management'].push({ key, label: displayValue, enabled: true });
            else if (key.startsWith('task.')) categories['Task Management'].push({ key, label: displayValue, enabled: true });
            else if (key.startsWith('file.')) categories['File Management'].push({ key, label: displayValue, enabled: true });
            else if (key.startsWith('notice.')) categories['Notices'].push({ key, label: displayValue, enabled: true });
            else if (key.startsWith('course.')) categories['Courses'].push({ key, label: displayValue, enabled: true });
            else if (key.startsWith('compliance.')) categories['Compliance'].push({ key, label: displayValue, enabled: true });
            else if (key.startsWith('analytics.')) categories['Analytics'].push({ key, label: displayValue, enabled: true });
            else if (key.startsWith('feedback.') || key.startsWith('survey.')) categories['Feedback & Surveys'].push({ key, label: displayValue, enabled: true });
            else if (key.startsWith('role.') || key.startsWith('team.')) categories['Roles & Teams'].push({ key, label: displayValue, enabled: true });
            else if (key.startsWith('system.')) categories['System Admin'].push({ key, label: displayValue, enabled: true });
            else if (key.startsWith('ai.')) categories['AI Assistant'].push({ key, label: displayValue, enabled: true });
            else if (key.startsWith('lifecycle.')) categories['Lifecycle'].push({ key, label: displayValue, enabled: true });
        });

        return Object.fromEntries(
            Object.entries(categories).filter(([_, perms]) => perms.length > 0)
        );
    };

    // State for permissions preview expansion
    const [showPermissionsPreview, setShowPermissionsPreview] = useState(false);

    // Toggle section helper
    const toggleSection = (sectionKey) => {
        setExpandedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
    };

    // Section IDs being selected to exclude from other dropdowns
    const getExcludeIds = (excludeField) => {
        const ids = [];
        ['manager', 'team_lead', 'buddy', 'hr_contact'].forEach(field => {
            if (field !== excludeField && formData[field]?.id) {
                ids.push(formData[field].id);
            }
        });
        // Also exclude custom placeholder assignments
        Object.entries(customPlaceholderAssignments).forEach(([phId, user]) => {
            if (excludeField !== `placeholder_${phId}` && user?.id) {
                ids.push(user.id);
            }
        });
        return ids;
    };

    if (!isOpen) return null;

    const inputClassName = (fieldName) =>
        `w-full pl-12 pr-4 py-3.5 border rounded-xl text-base focus:ring-2 focus:border-transparent outline-none transition-all ${validationErrors[fieldName] && touchedFields[fieldName]
            ? 'border-red-500 focus:ring-red-200'
            : 'border-border focus:ring-primary'
        }`;

    // Section header component
    const SectionHeader = ({ icon: Icon, title, subtitle, sectionKey, stepNumber, isRequired }) => (
        <button
            type="button"
            onClick={() => toggleSection(sectionKey)}
            className="w-full flex items-center justify-between p-5 hover:bg-muted/30 transition-colors rounded-xl"
        >
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">
                    {stepNumber}
                </div>
                <div className="text-left">
                    <h4 className="font-semibold text-foreground text-base flex items-center gap-2">
                        {title}
                        {isRequired && <span className="text-red-500 text-xs">*</span>}
                    </h4>
                    {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
                </div>
            </div>
            {expandedSections[sectionKey] ? (
                <ChevronUp size={20} className="text-muted-foreground" />
            ) : (
                <ChevronDown size={20} className="text-muted-foreground" />
            )}
        </button>
    );

    return (
        <>
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 md:p-4">
                <div className="bg-card rounded-none md:rounded-2xl shadow-2xl w-full max-w-4xl h-full md:h-auto md:max-h-[92vh] overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between p-5 md:p-7 border-b border-border flex-shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-primary/10 rounded-xl">
                                <UserPlus size={26} className="text-primary" />
                            </div>
                            <div>
                                <h2 className="text-xl md:text-2xl font-bold text-foreground">Add User</h2>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Add a new team member to your organisation
                                </p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
                            <X size={24} className="text-muted-foreground" />
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 md:p-7">
                        <div className="space-y-4">

                            {/* ─── SECTION 1: Basic Info ─── */}
                            <div className="border border-border rounded-xl overflow-visible">
                                <SectionHeader
                                    icon={User}
                                    title="Basic Information"
                                    subtitle="Name, email, and contact details"
                                    sectionKey="basicInfo"
                                    stepNumber={1}
                                    isRequired
                                />
                                {expandedSections.basicInfo && (
                                    <div className="px-4 pb-4 space-y-4">
                                        {/* Name Fields */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-semibold text-foreground mb-2.5">
                                                    First Name <span className="text-red-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                                                    <input
                                                        type="text"
                                                        name="first_name"
                                                        value={formData.first_name}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        className={`${inputClassName('first_name')} bg-background text-foreground placeholder:text-muted-foreground`}
                                                        placeholder="John"
                                                    />
                                                </div>
                                                {validationErrors.first_name && touchedFields.first_name && (
                                                    <p className="mt-1 text-sm text-red-500">{validationErrors.first_name}</p>
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-foreground mb-2.5">
                                                    Last Name <span className="text-red-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                                                    <input
                                                        type="text"
                                                        name="last_name"
                                                        value={formData.last_name}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        className={`${inputClassName('last_name')} bg-background text-foreground placeholder:text-muted-foreground`}
                                                        placeholder="Doe"
                                                    />
                                                </div>
                                                {validationErrors.last_name && touchedFields.last_name && (
                                                    <p className="mt-1 text-sm text-red-500">{validationErrors.last_name}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Email */}
                                        <div>
                                            <label className="block text-sm font-semibold text-foreground mb-2.5">
                                                Email <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <Mail size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    className={`${inputClassName('email')} bg-background text-foreground placeholder:text-muted-foreground`}
                                                    placeholder="john.doe@company.com"
                                                />
                                            </div>
                                            {validationErrors.email && touchedFields.email && (
                                                <p className="mt-1 text-sm text-red-500">{validationErrors.email}</p>
                                            )}
                                        </div>

                                        {/* Phone */}
                                        <div>
                                            <label className="block text-sm font-semibold text-foreground mb-2.5">Phone Number</label>
                                            <div className="relative">
                                                <Phone size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    className={`${inputClassName('phone')} bg-background text-foreground placeholder:text-muted-foreground`}
                                                    placeholder="+1 (555) 000-0000"
                                                />
                                            </div>
                                            {validationErrors.phone && touchedFields.phone && (
                                                <p className="mt-1 text-sm text-red-500">{validationErrors.phone}</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ─── SECTION 2: Role & Permissions ─── */}
                            <div className="border border-border rounded-xl overflow-visible">
                                <SectionHeader
                                    icon={Shield}
                                    title="Role & Permissions"
                                    subtitle="Account type and role assignment"
                                    sectionKey="rolePermissions"
                                    stepNumber={2}
                                />
                                {expandedSections.rolePermissions && (
                                    <div className="px-4 pb-4 space-y-4">
                                        {/* Account Type Selector */}
                                        <div>
                                            <label className="block text-sm font-semibold text-foreground mb-2.5">
                                                Account Type <span className="text-red-500">*</span>
                                            </label>
                                            <div className="grid grid-cols-2 gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => handleAccountTypeChange('employee')}
                                                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${formData.account_type === 'employee'
                                                        ? 'border-primary bg-primary/5 text-primary'
                                                        : 'border-border hover:border-primary/50 text-muted-foreground'
                                                        }`}
                                                >
                                                    <User size={24} />
                                                    <span className="font-semibold">Standard Employee</span>
                                                    <span className="text-xs text-muted-foreground">Standard team member</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleAccountTypeChange('admin')}
                                                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${formData.account_type === 'admin'
                                                        ? 'border-primary bg-primary/5 text-primary'
                                                        : 'border-border hover:border-primary/50 text-muted-foreground'
                                                        }`}
                                                >
                                                    <Shield size={24} />
                                                    <span className="font-semibold">Admin User</span>
                                                    <span className="text-xs text-muted-foreground">Administrative access</span>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Role Dropdown */}
                                        <div>
                                            <label className="block text-sm font-semibold text-foreground mb-2.5">Role</label>
                                            <div className="relative">
                                                <Briefcase size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                                                <select
                                                    name="role"
                                                    value={showInlineRoleCreation ? 'create_new' : (formData.role_id?.toString() || '')}
                                                    onChange={handleRoleChange}
                                                    className="w-full pl-11 pr-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all appearance-none bg-background text-foreground"
                                                >
                                                    <option value="">Select a role</option>
                                                    {getFilteredRoles().map((role) => (
                                                        <option
                                                            key={typeof role === 'string' ? role : role.id}
                                                            value={typeof role === 'string' ? role : role.id}
                                                        >
                                                            {typeof role === 'string' ? role : role.role_name}
                                                        </option>
                                                    ))}
                                                    <option value="create_new">+ Create New Role</option>
                                                </select>
                                                <ChevronDown size={20} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 pointer-events-none" />
                                            </div>
                                        </div>

                                        {/* Role Permissions Preview */}
                                        {formData.role_id && !showInlineRoleCreation && getSelectedRolePermissions() && (
                                            <div className="animate-in slide-in-from-top-2 duration-200 border border-border rounded-xl overflow-visible">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPermissionsPreview(!showPermissionsPreview)}
                                                    className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <Shield size={18} className="text-primary" />
                                                        <div className="text-left">
                                                            <h4 className="font-semibold text-foreground text-sm">Role Permissions</h4>
                                                            <p className="text-xs text-muted-foreground">
                                                                {Object.keys(getSelectedRolePermissions() || {}).filter(k => {
                                                                    const v = getSelectedRolePermissions()[k];
                                                                    return v && v !== 'none';
                                                                }).length} permissions enabled for "{formData.role}"
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {showPermissionsPreview ? (
                                                        <ChevronUp size={18} className="text-muted-foreground" />
                                                    ) : (
                                                        <ChevronDown size={18} className="text-muted-foreground" />
                                                    )}
                                                </button>

                                                {showPermissionsPreview && (
                                                    <div className="p-4 bg-card max-h-80 overflow-y-auto">
                                                        {(() => {
                                                            const permissions = getSelectedRolePermissions();
                                                            const grouped = groupPermissionsByCategory(permissions);
                                                            const categoryCount = Object.keys(grouped).length;

                                                            if (categoryCount === 0) {
                                                                return (
                                                                    <p className="text-sm text-muted-foreground text-center py-4">
                                                                        No permissions configured for this role
                                                                    </p>
                                                                );
                                                            }

                                                            return (
                                                                <div className="space-y-4">
                                                                    {Object.entries(grouped).map(([category, perms]) => (
                                                                        <div key={category}>
                                                                            <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                                                                                {category}
                                                                            </h5>
                                                                            <div className="flex flex-wrap gap-1.5">
                                                                                {perms.map((perm) => (
                                                                                    <span
                                                                                        key={perm.key}
                                                                                        className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-md border border-emerald-200"
                                                                                    >
                                                                                        <Check size={10} className="text-emerald-600" />
                                                                                        {perm.label}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Inline Role Creation Panel */}
                                        {showInlineRoleCreation && (
                                            <div className="animate-in slide-in-from-top-2 duration-200 bg-muted/30 rounded-xl p-4 border border-border">
                                                <div className="flex items-center gap-2 mb-4">
                                                    <Plus size={18} className="text-primary" />
                                                    <h4 className="font-semibold text-foreground">Create New Role</h4>
                                                </div>

                                                <div className="mb-4">
                                                    <label className="block text-sm font-medium text-foreground mb-1.5">
                                                        Role Name <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={inlineRoleName}
                                                        onChange={(e) => setInlineRoleName(e.target.value)}
                                                        className="w-full px-4 py-2.5 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-background text-foreground placeholder:text-muted-foreground"
                                                        placeholder="e.g., Marketing Manager"
                                                    />
                                                </div>

                                                <div className="mb-4">
                                                    <label className="block text-sm font-medium text-foreground mb-1.5">
                                                        Description (Optional)
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={inlineRoleDescription}
                                                        onChange={(e) => setInlineRoleDescription(e.target.value)}
                                                        className="w-full px-4 py-2.5 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-background text-foreground placeholder:text-muted-foreground"
                                                        placeholder="Brief description of this role"
                                                    />
                                                </div>

                                                <div className="mb-4">
                                                    <label className="block text-sm font-medium text-foreground mb-2">Permissions</label>
                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                                        {permissionCategories.slice(0, 6).map((category) => (
                                                            <button
                                                                key={category.key}
                                                                type="button"
                                                                onClick={() => handleInlinePermissionToggle(category.key)}
                                                                className={`p-3 rounded-lg border transition-all flex items-center gap-2 text-left ${inlineRolePermissions[category.key]
                                                                    ? 'border-primary bg-primary/10 text-primary'
                                                                    : 'border-border bg-card text-muted-foreground hover:border-primary/50'
                                                                    }`}
                                                            >
                                                                <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${inlineRolePermissions[category.key]
                                                                    ? 'bg-primary text-white'
                                                                    : 'bg-muted'
                                                                    }`}>
                                                                    {inlineRolePermissions[category.key] && <Check size={12} />}
                                                                </div>
                                                                <span className="text-xs font-medium truncate">
                                                                    {permissionCategoryLabels[category.key] || category.label}
                                                                </span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-between pt-3 border-t border-border">
                                                    <button
                                                        type="button"
                                                        onClick={handleCustomisePermissions}
                                                        className="text-sm text-primary hover:text-primary-dark font-medium flex items-center gap-1"
                                                    >
                                                        <Settings size={14} />
                                                        Customise permissions
                                                    </button>
                                                    <div className="flex gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowInlineRoleCreation(false)}
                                                            className="px-4 py-2 text-sm text-muted-foreground hover:bg-muted rounded-lg font-medium"
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={handleCreateRoleInline}
                                                            disabled={!inlineRoleName.trim() || loading}
                                                            className="px-4 py-2 text-sm bg-primary text-white rounded-lg font-medium hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                                        >
                                                            {loading ? (
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                            ) : (
                                                                <Check size={14} />
                                                            )}
                                                            Create Role
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Admin Info Banner */}
                                        {formData.account_type === 'admin' && (
                                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
                                                <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                                                <div className="text-sm text-blue-700">
                                                    <p className="font-semibold">Admin Account</p>
                                                    <p>This user will NOT appear in onboarding/preboarding stats. They will receive a setup email to set their password.</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* ─── SECTION 3: Organizational Structure ─── */}
                            <div className="border border-border rounded-xl overflow-visible">
                                <SectionHeader
                                    icon={Building2}
                                    title="Organizational Structure"
                                    subtitle="Team and manager assignment"
                                    sectionKey="orgStructure"
                                    stepNumber={3}
                                />
                                {expandedSections.orgStructure && (
                                    <div className="px-4 pb-4 space-y-4">
                                        {/* Team Selection */}
                                        {availableTeams.length > 0 && (
                                            <div>
                                                <label className="block text-sm font-semibold text-foreground mb-2.5">
                                                    <Users size={16} className="inline mr-2" />
                                                    Assign to Teams
                                                </label>
                                                <div className="flex flex-wrap gap-2">
                                                    {availableTeams.map((team) => (
                                                        <button
                                                            key={team.id}
                                                            type="button"
                                                            onClick={() => handleTeamToggle(team.id)}
                                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${formData.team_ids.includes(team.id)
                                                                ? 'bg-primary text-white'
                                                                : 'bg-muted text-foreground hover:bg-muted/80'
                                                                }`}
                                                        >
                                                            {team.team_name}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Manager Selection */}
                                        <UserSearchDropdown
                                            label="Manager / Supervisor"
                                            value={formData.manager}
                                            onChange={(user) => setFormData(prev => ({ ...prev, manager: user }))}
                                            placeholder="Search for a manager..."
                                            excludeIds={getExcludeIds('manager')}
                                            helpText="The manager who will oversee this employee"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* ─── SECTION 4: Onboarding Support Team ─── */}
                            {formData.account_type === 'employee' && (
                                <div className="border border-border rounded-xl overflow-visible">
                                    <SectionHeader
                                        icon={Heart}
                                        title="Onboarding Support Team"
                                        subtitle="Assign people to support this employee"
                                        sectionKey="supportTeam"
                                        stepNumber={4}
                                    />
                                    {expandedSections.supportTeam && (
                                        <div className="px-4 pb-4 space-y-4">
                                            {/* Built-in support roles */}
                                            <UserSearchDropdown
                                                label="Team Lead"
                                                value={formData.team_lead}
                                                onChange={(user) => setFormData(prev => ({ ...prev, team_lead: user }))}
                                                placeholder="Search for team lead..."
                                                excludeIds={getExcludeIds('team_lead')}
                                                helpText="The team lead who will guide this employee"
                                            />
                                            <UserSearchDropdown
                                                label="Buddy"
                                                value={formData.buddy}
                                                onChange={(user) => setFormData(prev => ({ ...prev, buddy: user }))}
                                                placeholder="Search for buddy..."
                                                excludeIds={getExcludeIds('buddy')}
                                                helpText="A peer who will help the new employee settle in"
                                            />
                                            <UserSearchDropdown
                                                label="HR Contact"
                                                value={formData.hr_contact}
                                                onChange={(user) => setFormData(prev => ({ ...prev, hr_contact: user }))}
                                                placeholder="Search for HR contact..."
                                                excludeIds={getExcludeIds('hr_contact')}
                                                helpText="Primary HR person for this employee"
                                            />

                                            {/* Custom placeholders */}
                                            {customPlaceholders.length > 0 && (
                                                <div className="border-t border-border pt-4 mt-4">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <label className="block text-sm font-semibold text-foreground">
                                                            Custom Roles
                                                        </label>
                                                    </div>
                                                    <div className="space-y-3">
                                                        {customPlaceholders.map((ph) => (
                                                            <div key={ph.id}>
                                                                {editingPlaceholder?.id === ph.id ? (
                                                                    <div className="flex items-center gap-2 mb-2">
                                                                        <input
                                                                            type="text"
                                                                            value={editingPlaceholder.name}
                                                                            onChange={(e) => setEditingPlaceholder(prev => ({ ...prev, name: e.target.value }))}
                                                                            className="flex-1 px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                                                                            placeholder="Role name"
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleUpdatePlaceholder(ph.id)}
                                                                            className="p-2 text-primary hover:bg-primary/10 rounded-lg"
                                                                        >
                                                                            <Check size={16} />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setEditingPlaceholder(null)}
                                                                            className="p-2 text-muted-foreground hover:bg-muted rounded-lg"
                                                                        >
                                                                            <X size={16} />
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex items-center gap-1 mb-1.5">
                                                                        <span className="text-sm font-medium text-foreground flex-1">
                                                                            {ph.name}
                                                                            {ph.description && (
                                                                                <span className="text-xs text-muted-foreground ml-2">— {ph.description}</span>
                                                                            )}
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setEditingPlaceholder({ id: ph.id, name: ph.name, description: ph.description })}
                                                                            className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded"
                                                                            title="Edit"
                                                                        >
                                                                            <Pencil size={13} />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleDeletePlaceholder(ph.id)}
                                                                            className="p-1 text-muted-foreground hover:text-red-500 hover:bg-red-50 rounded"
                                                                            title="Delete"
                                                                        >
                                                                            <Trash2 size={13} />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                                <UserSearchDropdown
                                                                    value={customPlaceholderAssignments[ph.id] || null}
                                                                    onChange={(user) => setCustomPlaceholderAssignments(prev => ({ ...prev, [ph.id]: user }))}
                                                                    placeholder={`Search for ${ph.name.toLowerCase()}...`}
                                                                    excludeIds={getExcludeIds(`placeholder_${ph.id}`)}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Add custom placeholder */}
                                            {showAddPlaceholder ? (
                                                <div className="border border-dashed border-border rounded-xl p-4 bg-muted/20">
                                                    <div className="space-y-3">
                                                        <input
                                                            type="text"
                                                            value={newPlaceholderName}
                                                            onChange={(e) => setNewPlaceholderName(e.target.value)}
                                                            className="w-full px-4 py-2.5 border border-border rounded-lg text-sm bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent outline-none placeholder:text-muted-foreground"
                                                            placeholder="Role name (e.g. IT Admin, Safety Officer)"
                                                            autoFocus
                                                        />
                                                        <input
                                                            type="text"
                                                            value={newPlaceholderDescription}
                                                            onChange={(e) => setNewPlaceholderDescription(e.target.value)}
                                                            className="w-full px-4 py-2.5 border border-border rounded-lg text-sm bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent outline-none placeholder:text-muted-foreground"
                                                            placeholder="Description (optional)"
                                                        />
                                                        <div className="flex gap-2 justify-end">
                                                            <button
                                                                type="button"
                                                                onClick={() => { setShowAddPlaceholder(false); setNewPlaceholderName(''); setNewPlaceholderDescription(''); }}
                                                                className="px-4 py-2 text-sm text-muted-foreground hover:bg-muted rounded-lg font-medium"
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={handleCreatePlaceholder}
                                                                disabled={!newPlaceholderName.trim()}
                                                                className="px-4 py-2 text-sm bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                                            >
                                                                <Check size={14} />
                                                                Add Role
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => setShowAddPlaceholder(true)}
                                                    className="w-full py-2.5 border border-dashed border-border rounded-xl text-sm font-medium text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors flex items-center justify-center gap-2"
                                                >
                                                    <Plus size={16} />
                                                    Add Custom Support Role
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ─── SECTION 5: Onboarding Journey ─── */}
                            {formData.account_type === 'employee' && (
                                <div className="border border-border rounded-xl overflow-visible">
                                    <SectionHeader
                                        icon={ArrowRight}
                                        title="Onboarding Journey"
                                        subtitle="Lifecycle stage and start date"
                                        sectionKey="journey"
                                        stepNumber={formData.account_type === 'employee' ? 5 : 4}
                                        isRequired
                                    />
                                    {expandedSections.journey && (
                                        <div className="px-4 pb-4 space-y-4">
                                            {/* Lifecycle Stage */}
                                            <div>
                                                <label className="block text-sm font-semibold text-foreground mb-2.5">
                                                    Lifecycle Stage <span className="text-red-500">*</span>
                                                </label>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleLifecycleStageChange('preboarding')}
                                                        className={`px-4 py-3.5 rounded-xl border-2 transition-all text-center ${formData.lifecycle_stage === 'preboarding'
                                                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                                                            : 'border-border hover:border-primary/50 text-muted-foreground'
                                                            }`}
                                                    >
                                                        <span className="font-medium text-sm">Pre-boarding</span>
                                                        <p className="text-xs mt-0.5 opacity-70">Before start date</p>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleLifecycleStageChange('onboarding')}
                                                        className={`px-4 py-3.5 rounded-xl border-2 transition-all text-center ${formData.lifecycle_stage === 'onboarding'
                                                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                            : 'border-border hover:border-primary/50 text-muted-foreground'
                                                            }`}
                                                    >
                                                        <span className="font-medium text-sm">Onboarding</span>
                                                        <p className="text-xs mt-0.5 opacity-70">Starting immediately</p>
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Start Date — Preboarding only */}
                                            {formData.lifecycle_stage === 'preboarding' && (
                                                <div className="animate-in slide-in-from-top-2 duration-200">
                                                    <label className="block text-sm font-semibold text-foreground mb-2.5">
                                                        Start Date <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="relative">
                                                        <Calendar size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                                                        <input
                                                            type="date"
                                                            name="start_date"
                                                            value={formData.start_date}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            min={getTomorrowDate()}
                                                            className={`${inputClassName('start_date')} bg-background text-foreground`}
                                                        />
                                                    </div>
                                                    {validationErrors.start_date && touchedFields.start_date && (
                                                        <p className="mt-1 text-sm text-red-500">{validationErrors.start_date}</p>
                                                    )}
                                                    <div className="mt-2 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-start gap-2">
                                                        <Info size={16} className="text-purple-600 flex-shrink-0 mt-0.5" />
                                                        <p className="text-xs text-purple-700">
                                                            Employee will automatically transition to Onboarding on this date
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ─── SECTION 6: Additional Details ─── */}
                            <div className="border border-border rounded-xl overflow-visible">
                                <SectionHeader
                                    icon={FileText}
                                    title="Additional Details"
                                    subtitle="Notes and extra information"
                                    sectionKey="additionalDetails"
                                    stepNumber={formData.account_type === 'employee' ? 6 : 4}
                                />
                                {expandedSections.additionalDetails && (
                                    <div className="px-4 pb-4">
                                        <label className="block text-sm font-semibold text-foreground mb-2.5">Notes</label>
                                        <div className="relative">
                                            <FileText size={20} className="absolute left-3 top-3 text-muted-foreground/70" />
                                            <textarea
                                                name="additional_details"
                                                value={formData.additional_details}
                                                onChange={handleChange}
                                                rows={3}
                                                className="w-full pl-11 pr-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none bg-background text-foreground placeholder:text-muted-foreground"
                                                placeholder="Any additional notes..."
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </form>

                    {/* Footer */}
                    <div className="flex items-center gap-3 p-5 md:p-7 border-t border-border bg-muted/30 flex-shrink-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 md:flex-none px-7 py-3.5 text-foreground hover:bg-muted rounded-xl font-semibold transition-colors border border-border md:border-transparent bg-card md:bg-transparent text-base"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={loading || showInlineRoleCreation}
                            className="flex-1 md:flex-auto px-7 py-3.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-semibold shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <ArrowRight size={20} />
                                    Continue to Email Preview
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Email Preview Modal */}
            {showEmailPreview && pendingUserData && (
                <EmailPreviewModal
                    isOpen={showEmailPreview}
                    onClose={() => setShowEmailPreview(false)}
                    onSend={handleEmailSend}
                    employeeData={pendingUserData}
                    companyData={companyData}
                    isPreboarding={pendingUserData.lifecycle_stage === 'preboarding'}
                    isUserInvitation={formData.account_type === 'admin'}
                />
            )}

            {/* Full Create Role Modal */}
            {showCreateRoleModal && (
                <CreateRoleModal
                    isOpen={showCreateRoleModal}
                    onClose={() => {
                        setShowCreateRoleModal(false);
                        setInlineRoleData(null);
                    }}
                    onSuccess={handleFullRoleCreated}
                    initialData={inlineRoleData}
                    isEditing={false}
                />
            )}
        </>
    );
};

export default UnifiedAddEmployeeModal;
